import { apiGet, apiGetBlob, apiPost, apiPatch, apiDelete } from "../api-client"

// ---- C-103 Form ----

export type C103FormType = 'noi' | 'subsequent'

export type C103FormStatus =
  | 'draft'
  | 'internal_review'
  | 'engineer_approved'
  | 'filed'
  | 'agency_approved'
  | 'agency_rejected'
  | 'revision_requested'

export type LeaseType = 'state' | 'fee' | 'federal' | 'indian'

export interface C103Form {
  id: number
  well: number | null
  api_number: string
  form_type: C103FormType
  status: C103FormStatus
  region: string
  sub_area: string | null
  coa_figure: string
  lease_type: LeaseType
  plan_data: Record<string, unknown>
  proposed_work_narrative: string
  compliance_violations: string[]
  plan_snapshot: number | null
  created_at: string
  updated_at: string
  submitted_at: string | null
  plugs?: C103Plug[]
  events?: C103Event[]
}

export interface C103FormListItem {
  id: number
  api_number: string
  form_type: C103FormType
  status: C103FormStatus
  region: string
  coa_figure: string
  lease_type: LeaseType
  created_at: string
  updated_at: string
  submitted_at: string | null
}

export interface C103FormCreateRequest {
  well?: number
  api_number: string
  form_type?: C103FormType
  region?: string
  sub_area?: string
  coa_figure?: string
  lease_type?: LeaseType
  plan_data?: Record<string, unknown>
  proposed_work_narrative?: string
}


// ---- C-103 Plug ----

export type C103OperationType = 'spot' | 'squeeze' | 'circulate'
export type C103HoleType = 'cased' | 'open'
export type C103StepType =
  | 'cement_plug' | 'formation_plug' | 'cibp_cap' | 'shoe_plug'
  | 'surface_plug' | 'duqw_plug' | 'fill_plug' | 'mechanical_plug'

export interface C103Plug {
  id: number
  c103_form: number
  plug_number: number
  step_type: C103StepType
  operation_type: C103OperationType
  hole_type: C103HoleType
  top_ft: number
  bottom_ft: number
  cement_class: string
  sacks_required: number
  inside_sacks: number | null
  outside_sacks: number | null
  excess_factor: number
  formation_name: string
  tag_required: boolean
  wait_hours: number
  procedure_narrative: string
  regulatory_basis: string
  casing_size_in: number | null
}


// ---- C-103 Event ----

export type C103EventType =
  | 'set_cement_plug' | 'set_surface_plug' | 'set_bridge_plug' | 'set_marker'
  | 'squeeze' | 'tag_toc' | 'circulate' | 'pump_cement' | 'woc'
  | 'pressure_test' | 'pull_tubing' | 'cut_casing'

export interface C103Event {
  id: number
  well: number | null
  c103_form: number | null
  api_number: string
  event_type: C103EventType
  event_date: string
  event_start_time: string | null
  event_end_time: string | null
  depth_top_ft: number | null
  depth_bottom_ft: number | null
  tagged_depth_ft: number | null
  cement_class: string
  sacks: number | null
  volume_bbl: number | null
  pressure_psi: number | null
  plug_number: number | null
  raw_event_detail: string
  casing_string: string
}


// ---- C-103 Form API ----

export async function listC103Forms(params?: {
  api_number?: string
  form_type?: C103FormType
  status?: C103FormStatus
}): Promise<C103FormListItem[]> {
  const query = new URLSearchParams()
  if (params?.api_number) query.set('api_number', params.api_number)
  if (params?.form_type) query.set('form_type', params.form_type)
  if (params?.status) query.set('status', params.status)
  const qs = query.toString()
  return apiGet<C103FormListItem[]>(`/api/c103/forms/${qs ? `?${qs}` : ''}`)
}

export async function getC103Form(formId: number): Promise<C103Form> {
  return apiGet<C103Form>(`/api/c103/forms/${formId}/`)
}

export async function createC103Form(data: C103FormCreateRequest): Promise<C103Form> {
  return apiPost<C103Form>('/api/c103/forms/', data)
}

export async function updateC103Form(formId: number, data: Partial<C103FormCreateRequest>): Promise<C103Form> {
  return apiPatch<C103Form>(`/api/c103/forms/${formId}/`, data)
}

export async function deleteC103Form(formId: number): Promise<void> {
  await apiDelete(`/api/c103/forms/${formId}/`)
}

export async function submitC103Form(formId: number, data?: {
  submitted_by?: string
  nmocd_confirmation_number?: string
  notes?: string
}): Promise<C103Form> {
  return apiPost<C103Form>(`/api/c103/forms/${formId}/submit/`, data ?? {})
}

export async function getC103FormsByApi(apiNumber: string): Promise<C103FormListItem[]> {
  const response = await apiGet<{ api_number: string; count: number; forms: C103FormListItem[] }>(
    `/api/c103/forms/by_api/?api_number=${encodeURIComponent(apiNumber)}`
  )
  return response.forms
}

export async function getPendingC103Forms(): Promise<C103FormListItem[]> {
  const response = await apiGet<{ count: number; forms: C103FormListItem[] }>(
    '/api/c103/forms/pending_submission/'
  )
  return response.forms
}

export async function getFiledC103Forms(): Promise<C103FormListItem[]> {
  const response = await apiGet<{ count: number; forms: C103FormListItem[] }>(
    '/api/c103/forms/filed/'
  )
  return response.forms
}

export async function generateC103PDF(formId: number): Promise<Blob> {
  return apiGetBlob(`/api/c103/forms/${formId}/export-pdf/`)
}


// ---- C-103 Plug API ----

export async function listC103Plugs(formId: number): Promise<C103Plug[]> {
  return apiGet<C103Plug[]>(`/api/c103/forms/${formId}/plugs/`)
}

export async function createC103Plug(formId: number, data: Partial<C103Plug>): Promise<C103Plug> {
  return apiPost<C103Plug>(`/api/c103/forms/${formId}/plugs/`, data)
}

export async function updateC103Plug(formId: number, plugId: number, data: Partial<C103Plug>): Promise<C103Plug> {
  return apiPatch<C103Plug>(`/api/c103/forms/${formId}/plugs/${plugId}/`, data)
}

export async function deleteC103Plug(formId: number, plugId: number): Promise<void> {
  await apiDelete(`/api/c103/forms/${formId}/plugs/${plugId}/`)
}


// ---- C-103 Event API ----

export async function listC103Events(params?: {
  api_number?: string
  event_type?: C103EventType
  c103_form?: number
}): Promise<C103Event[]> {
  const query = new URLSearchParams()
  if (params?.api_number) query.set('api_number', params.api_number)
  if (params?.event_type) query.set('event_type', params.event_type)
  if (params?.c103_form !== undefined) query.set('c103_form', String(params.c103_form))
  const qs = query.toString()
  return apiGet<C103Event[]>(`/api/c103/events/${qs ? `?${qs}` : ''}`)
}

export async function createC103Event(data: Partial<C103Event>): Promise<C103Event> {
  return apiPost<C103Event>('/api/c103/events/', data)
}

export async function updateC103Event(eventId: number, data: Partial<C103Event>): Promise<C103Event> {
  return apiPatch<C103Event>(`/api/c103/events/${eventId}/`, data)
}

export async function deleteC103Event(eventId: number): Promise<void> {
  await apiDelete(`/api/c103/events/${eventId}/`)
}
